import React from 'react';

import {
    Checkbox,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Select,
    Typography,
    type SelectChangeEvent,
    TextField,
    InputLabel,
    styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import OverrideControls from './OverrideControls';
import ZoomAdminSettings from './ZoomAdminSettings';
import { ClassLevelOverrideOptions } from '../../../types/adminConfig';
import AdminSettingsContainer from '../AdminSettingsContainer';
import SaveButton from '../components/SaveButton';
import { StyledFormControlLabel, StyledFormGroupContainer, StyledHelperText, StyledSubgrouping } from '../styles';
import StyledFormTitle from '../styles/form-title';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const StyledFormDiv = styled('div')`
    display: flex;
    align-items: center;
    margin-top: 1rem;
`;

const ZoomSettings = () => {
    const { adminSettings, setAdminSettings, isSavingSettingsData, isSavingDisabled, handleSaveSettings } =
        useContainer(AdminSettingsContainer);
    const { t } = useTranslation('admin_settings');
    const { zoomSettings, authSettings, featureSettings } = adminSettings;
    const { overrides, options } = featureSettings;
    const { allowVanillaZoom = false } = options;
    const handleAuthSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        zoomSettings[event.target.name] = event.target.checked;

        if (event.target.name === 'onlyNumericPasscode' && event.target.checked) {
            zoomSettings.requireNumber = true;
            zoomSettings.requireSpecialCharacter = false;
            zoomSettings.requireLetter = false;
            zoomSettings.requireUpperLowerCase = false;
        }

        setAdminSettings({ ...adminSettings });
    };
    const handleAuthCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        authSettings[event.target.name] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };
    const handleSelectChange = (event: SelectChangeEvent<number>) => {
        if (!event.target.name) {
            return;
        }
        zoomSettings[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsBool = (event: React.ChangeEvent<HTMLInputElement>) => {
        options[event.target.name] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOverrides = (event: React.ChangeEvent<HTMLInputElement>) => {
        overrides[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
    };

    return (
        <>
            <StyledFormTitle>{t('zoomSettings.title')}</StyledFormTitle>
            <StyledFormGroupContainer>
                <FormGroup>
                    <Typography variant="subtitle1">{t('zoomSettings.meeting-passscode')}</Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={zoomSettings.useCustomPasscodeRequirements}
                                onChange={handleChange}
                                name="useCustomPasscodeRequirements"
                            />
                        }
                        label={t('zoomSettings.useCustomPasscodeRequirements')}
                    />
                    {zoomSettings.useCustomPasscodeRequirements && (
                        <StyledSubgrouping>
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.minPasscodeLength}
                                        onChange={handleChange}
                                        name="minPasscodeLength"
                                        id="minPasscodeLength"
                                    />
                                }
                                label={t('zoomSettings.minPasscodeLength')}
                            />
                            {zoomSettings.minPasscodeLength && (
                                <StyledFormDiv>
                                    <StyledFormControlLabel
                                        labelPlacement="start"
                                        label={t('zoomSettings.specify-passcode-length')}
                                        control={
                                            <Select
                                                value={zoomSettings.passcodeLength}
                                                onChange={handleSelectChange}
                                                name="passcodeLength"
                                                autoWidth={true}
                                            >
                                                {[6, 7, 8, 9, 10].map((length) => (
                                                    <MenuItem key={length} value={length}>
                                                        {length}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        }
                                    />
                                </StyledFormDiv>
                            )}
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.requireLetter}
                                        onChange={handleChange}
                                        name="requireLetter"
                                        disabled={zoomSettings.onlyNumericPasscode}
                                    />
                                }
                                label={t('zoomSettings.requireLetter')}
                            />
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.requireNumber}
                                        onChange={handleChange}
                                        name="requireNumber"
                                        disabled={zoomSettings.onlyNumericPasscode}
                                    />
                                }
                                label={t('zoomSettings.requireNumber')}
                            />
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.requireSpecialCharacter}
                                        onChange={handleChange}
                                        name="requireSpecialCharacter"
                                        disabled={zoomSettings.onlyNumericPasscode}
                                    />
                                }
                                label={t('zoomSettings.requireSpecialCharacter')}
                            />
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.requireUpperLowerCase}
                                        onChange={handleChange}
                                        name="requireUpperLowerCase"
                                        disabled={zoomSettings.onlyNumericPasscode}
                                    />
                                }
                                label={t('zoomSettings.requireUpperLowerCase')}
                            />
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.noConsecutiveCharacters}
                                        onChange={handleChange}
                                        name="noConsecutiveCharacters"
                                    />
                                }
                                label={t('zoomSettings.noConsecutiveCharacters')}
                            />
                            {zoomSettings.noConsecutiveCharacters && (
                                <StyledFormDiv>
                                    <StyledFormControlLabel
                                        labelPlacement="start"
                                        label={t('zoomSettings.specify-consecutive-character-length')}
                                        control={
                                            <Select
                                                value={zoomSettings.consecutiveCharacterLength}
                                                onChange={handleSelectChange}
                                                name="consecutiveCharacterLength"
                                                autoWidth={true}
                                            >
                                                {[4, 5, 6, 7, 8].map((length) => (
                                                    <MenuItem key={length} value={length}>
                                                        {length}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        }
                                    />
                                </StyledFormDiv>
                            )}
                            <AdminControlCheckbox
                                control={
                                    <Checkbox
                                        checked={zoomSettings.onlyNumericPasscode}
                                        onChange={handleChange}
                                        name="onlyNumericPasscode"
                                    />
                                }
                                label={t('zoomSettings.onlyNumericPasscode')}
                            />
                        </StyledSubgrouping>
                    )}
                </FormGroup>
            </StyledFormGroupContainer>
            <StyledFormGroupContainer>
                <Typography variant="subtitle1">{t('auth.zoom-authentication')}</Typography>
                <ZoomAdminSettings />
                <StyledFormGroupContainer>
                    <AdminControlCheckbox
                        control={
                            <Checkbox
                                checked={authSettings.forcedAuthentication}
                                onChange={handleAuthCheckboxChange}
                                id="forcedAuthentication"
                                name="forcedAuthentication"
                            />
                        }
                        label={t('auth.forcedAuthenticationControl')}
                    />
                    <StyledHelperText variant="body2">{t('auth.helperTextLine1')}</StyledHelperText>
                </StyledFormGroupContainer>
            </StyledFormGroupContainer>
            <StyledFormGroupContainer>
                <InputLabel htmlFor="vanityUrl">{t('auth.vanityUrl')}</InputLabel>
                <TextField
                    value={authSettings.vanityUrl}
                    onChange={handleAuthSettingsChange}
                    id="vanityUrl"
                    name="vanityUrl"
                    size="small"
                />
            </StyledFormGroupContainer>
            <StyledFormGroupContainer>
                <Typography variant="subtitle1">{t('zoomSettings.meetingOptions')}</Typography>
                <AdminControlCheckbox
                    control={
                        <Checkbox
                            color="primary"
                            checked={allowVanillaZoom}
                            onChange={handleUpdateOptionsBool}
                            id="allowVanillaZoom"
                            name="allowVanillaZoom"
                        />
                    }
                    label={t('configuration.allow-vanilla-zoom') as string}
                />
                <OverrideControls
                    overrideName="allowVanillaZoom"
                    overrideValue={overrides.allowVanillaZoom ?? ClassLevelOverrideOptions.NoOne}
                    handleUpdateOverrides={handleUpdateOverrides}
                    labelText={t('configuration.allow-vanilla-zoom-override')}
                    tooltipText={t('configuration.no-one-allow-vanilla-zoom-tooltip')}
                />
            </StyledFormGroupContainer>
            <StyledFormGroupContainer>
                <SaveButton isSaving={isSavingSettingsData} disabled={isSavingDisabled} onClick={handleSaveSettings} />
            </StyledFormGroupContainer>
        </>
    );
};

export default ZoomSettings;
